// =============================================================================
// Clerk PIN gate (legacy frmLoginPopUp) — shared across the Sales Register and
// Payouts. Pick the clerk name and enter the PIN (which IS the clerk's auth
// password). The PIN is verified against Supabase Auth on an isolated client;
// on success the verified clerk (with auth user id) is returned to the caller so
// it can be stamped on the record (invoices.clerk_id / payouts.issued_from_id).
// =============================================================================
import { useEffect, useRef, useState } from 'react'
import { KeyRound, X } from 'lucide-react'
import { Card, Button } from './ui'
import { listClerks, verifyClerkPin, type Clerk } from '../../services/data'

export default function ClerkPinModal({ onClose, onVerified, title = 'Clerk Sign-In' }: {
  onClose: () => void
  onVerified: (c: Clerk) => void
  title?: string
}) {
  const [clerks, setClerks] = useState<Clerk[] | null>(null)
  const [selected, setSelected] = useState('')   // clerk email
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const pinRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    listClerks().then(setClerks).catch(() => setClerks([]))
  }, [])

  const submit = async () => {
    setError('')
    const clerk = (clerks ?? []).find(c => c.email === selected)
    if (!clerk) { setError('Select a clerk.'); return }
    if (!pin) { setError('Enter your PIN.'); pinRef.current?.focus(); return }
    setBusy(true)
    try {
      const id = await verifyClerkPin(clerk.email, pin)
      if (!id) { setError('Invalid PIN.'); setPin(''); pinRef.current?.focus(); return }
      onVerified({ ...clerk, id })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg flex items-center gap-2"><KeyRound size={18} /> {title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Clerk</label>
            <select
              value={selected}
              onChange={e => { setSelected(e.target.value); setError(''); pinRef.current?.focus() }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white text-gray-900"
              autoFocus
            >
              <option value="">{clerks === null ? 'Loading…' : 'Select a clerk…'}</option>
              {(clerks ?? []).map(c => <option key={c.email} value={c.email}>{c.username}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PIN</label>
            <input
              ref={pinRef}
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={e => { setPin(e.target.value); setError('') }}
              onKeyDown={e => { if (e.key === 'Enter') submit() }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-lg tracking-widest bg-white text-gray-900"
              placeholder="••••"
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !selected}>{busy ? 'Verifying…' : 'Sign in'}</Button>
        </div>
      </Card>
    </div>
  )
}
